/**
 * PostMessage Authentication Provider Module
 * Provides authentication using API keys received via postMessage
 * Used for secure iframe embedding where API keys should not appear in URLs
 */

import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react'
import { api } from '@/utils/api-client'
import { setAuthTokens, clearAuthTokens } from '@/utils/auth-storage'
import LoadingSpinner from './loading-spinner'

/**
 * Props for the PostMessageAuthProvider component
 * @interface PostMessageAuthProviderProps
 * @property {ReactNode} children - Child components to render after authentication
 */
interface PostMessageAuthProviderProps {
  children: ReactNode
}

/**
 * User data structure from authentication
 * @interface PostMessageUser
 */
interface PostMessageUser {
  id: string
  name: string
  email: string
}

/**
 * Workspace data structure from authentication
 * @interface PostMessageWorkspace
 */
interface PostMessageWorkspace {
  id: string
  name: string
}

/**
 * Context for PostMessage authentication state
 * @interface PostMessageAuthContext
 */
interface PostMessageAuthContext {
  user: PostMessageUser | null
  workspace: PostMessageWorkspace | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

/**
 * Default context values
 */
const defaultContextValue: PostMessageAuthContext = {
  user: null,
  workspace: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
}

/**
 * React context for PostMessage authentication
 */
const PostMessageAuthContext = createContext<PostMessageAuthContext>(defaultContextValue)

/**
 * Hook to access PostMessage authentication context
 * @returns {PostMessageAuthContext} The current authentication state
 */
export function usePostMessageAuth(): PostMessageAuthContext {
  const context = useContext(PostMessageAuthContext)

  if (!context) {
    throw new Error('usePostMessageAuth must be used within a PostMessageAuthProvider')
  }

  return context
}

/**
 * PostMessage Authentication Provider Component
 * Waits for API key via postMessage, then authenticates
 *
 * @param {PostMessageAuthProviderProps} props - Component props
 * @returns {JSX.Element} Provider component
 */
export function PostMessageAuthProvider({ children }: PostMessageAuthProviderProps) {
  const [user, setUser] = useState<PostMessageUser | null>(null)
  const [workspace, setWorkspace] = useState<PostMessageWorkspace | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [waitingForAuth, setWaitingForAuth] = useState(true)

  const isAuthenticated = user !== null && workspace !== null

  /**
   * Exchange a credential for a Bearer token and validate the session.
   * Prefers the PUBLIC embed key (exchanged via /public/embed-config/session);
   * falls back to the legacy secret API key path for back-compat.
   */
  const authenticateWithCredential = useCallback(async (
    credential: { embedKey?: string; apiKey?: string }
  ) => {
    try {
      setIsLoading(true)
      setError(null)

      // Step 1: Exchange credential for Bearer token. Public embed key first.
      const tokenResponse = credential.embedKey
        ? await api.post('/public/embed-config/session', {
            embedKey: credential.embedKey
          }, {
            requiresAuth: false,
            showErrorToast: false
          })
        : await api.post('/auth/api-key-token', {
            apiKey: credential.apiKey
          }, {
            requiresAuth: false,
            showErrorToast: false
          })

      if (!tokenResponse.success || !tokenResponse.data) {
        throw new Error(tokenResponse.error || 'Failed to exchange API key for token')
      }

      const { accessToken, refreshToken, expiresAt } = tokenResponse.data

      // Step 2: Store tokens
      setAuthTokens({
        accessToken,
        refreshToken,
        expiresAt: new Date(expiresAt).getTime()
      })

      // Step 3: Validate session
      const sessionResponse = await api.get('/auth/session', {
        requiresAuth: true,
        showErrorToast: false
      })

      if (sessionResponse.success && sessionResponse.data) {
        const { user: userData, workspace: workspaceData } = sessionResponse.data

        setUser({
          id: userData.id,
          name: userData.name || userData.email,
          email: userData.email
        })

        setWorkspace({
          id: workspaceData.id,
          name: workspaceData.name
        })

        // Notify parent of successful authentication
        if (window.parent !== window) {
          window.parent.postMessage({
            type: 'taqat-embed-authenticated'
          }, '*')
        }
      } else {
        throw new Error('Failed to validate session after token exchange')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Authentication failed'
      setError(errorMessage)
      setUser(null)
      setWorkspace(null)
      clearAuthTokens()

      // Notify parent of error
      if (window.parent !== window) {
        window.parent.postMessage({
          type: 'taqat-embed-error',
          error: errorMessage
        }, '*')
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Setup postMessage listener and notify parent we're ready
   */
  useEffect(() => {
    // Only proceed if we're in an iframe
    if (window.parent === window) {
      setError('PostMessage auth requires being in an iframe')
      setIsLoading(false)
      return
    }

    const handleMessage = (event: MessageEvent) => {
      // Handle authentication message
      if (event.data?.type === 'taqat-embed-auth') {
        const { embedKey, apiKey } = event.data.payload || {}

        if (!embedKey && !apiKey) {
          setError('No embed key received')
          setIsLoading(false)
          return
        }

        setWaitingForAuth(false)
        authenticateWithCredential({ embedKey, apiKey })
      }
    }

    // Listen for messages
    window.addEventListener('message', handleMessage)

    // Notify parent that we're ready to receive authentication
    window.parent.postMessage({
      type: 'taqat-embed-ready'
    }, '*')

    // Set a timeout for receiving auth
    const timeout = setTimeout(() => {
      if (waitingForAuth) {
        setError('Timeout waiting for authentication from parent')
        setIsLoading(false)
      }
    }, 10000) // 10 second timeout

    return () => {
      window.removeEventListener('message', handleMessage)
      clearTimeout(timeout)
    }
  }, [authenticateWithCredential, waitingForAuth])

  const contextValue: PostMessageAuthContext = {
    user,
    workspace,
    isAuthenticated,
    isLoading,
    error
  }

  // Show loading while waiting for auth or authenticating
  if (isLoading) {
    return <LoadingSpinner message={waitingForAuth ? "Waiting for authentication..." : "Authenticating..."} />
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center p-6 max-w-md">
          <h2 className="text-lg font-semibold mb-2 text-destructive">
            Authentication Failed
          </h2>
          <p className="text-muted-foreground mb-4">
            {error}
          </p>
          <p className="text-xs text-muted-foreground">
            Please ensure the embed is properly configured.
          </p>
        </div>
      </div>
    )
  }

  // Show error if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center p-6 max-w-md">
          <h2 className="text-lg font-semibold mb-2 text-destructive">
            Authentication Error
          </h2>
          <p className="text-muted-foreground">
            Unable to authenticate. Please refresh and try again.
          </p>
        </div>
      </div>
    )
  }

  return (
    <PostMessageAuthContext.Provider value={contextValue}>
      {children}
    </PostMessageAuthContext.Provider>
  )
}

PostMessageAuthProvider.displayName = 'PostMessageAuthProvider'
